import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/hooks/use-toast';
import { useBusinessAccountContext } from '@/hooks/useBusinessAccountContext';
import { razorpayApi } from '@/lib/api/services/razorpay';
import { friendlyMessage, showApiError } from '@/lib/errors';
import { validatePaymentAmount, MAX_PAYMENT_LINK_AMOUNT } from '@/lib/constants/paymentLimits';
import { Loader2, RefreshCw, PlugZap, Unplug, ArrowLeft, Key, CheckCircle, XCircle, Clock, Link as LinkIcon, ExternalLink, Copy, CreditCard, CalendarClock, Package, Play, Pause, Ban, Plus, Webhook, Check, AlertTriangle } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import DataTable, { Column } from '@/components/reusable/DataTable';
import { ContactsPagination } from '@/components/contacts/ContactsPagination';
import { Dialog, ScrollableDialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/scrollable-dialog';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { DisconnectConfirmDialog } from '@/components/integrations/DisconnectConfirmDialog';
import { motion } from 'framer-motion';
import { SearchBar } from '@/components/reusable/SearchBar';

type Payment = {
  id: string;
  amount: number;
  currency: string;
  status: string;
  method?: string;
  payment_method?: string;
  description?: string;
  created_at: string | number | Date;
  order_id?: string;
  customer_id?: string;
  razorpay_payment_id?: string;
  contact?: {
    name?: string;
    phone?: string;
    email?: string;
  };
};

type PaymentLink = {
  id: string;
  short_url: string;
  amount: number;
  currency: string;
  description?: string;
  status: string;
  created_at: string | number | Date;
  updated_at?: string | number | Date;
  amount_paid?: number;
  customer?: {
    name?: string;
    email?: string;
    contact?: string;
  } | [];
};

type Plan = {
  id: string;
  entity: string;
  interval: number;
  period: string;
  item: {
    id: string;
    active: boolean;
    amount: number;
    unit_amount: number;
    currency: string;
    name: string;
    description?: string;
  };
  notes: Record<string, any>;
  created_at: number;
};

type Subscription = {
  id: string;
  entity: string;
  plan_id: string;
  customer_id?: string;
  status: string;
  current_start?: number | null;
  current_end?: number | null;
  ended_at?: number | null;
  quantity: number;
  notes: Record<string, any>;
  charge_at?: number;
  remaining_count?: number;
  paid_count?: number;
  total_count?: number;
  customer_notify?: number;
  created_at: number;
  short_url?: string;
  has_scheduled_changes?: boolean;
  payment_method?: string;
  offer_id?: string | null;
};

interface RazorpayPageProps {
  defaultTab?: string;
  hideTabs?: boolean;
}

// The public URL Razorpay must POST webhook events to. Built from the same
// VITE_API_BASE_URL the rest of the app uses (already includes `/api/v1`), so it
// resolves to `https://<backend>/api/v1/webhooks/razorpay` in every environment.
// The backend registers this path (and the root `/webhooks/razorpay`) in app.js.
const RAZORPAY_WEBHOOK_URL = (() => {
  const base = (import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api/v1').replace(/\/+$/, '');
  return `${base}/webhooks/razorpay`;
})();

// Events RazorpayWebhookService actually handles. Core = one-time payments +
// links + refunds (needed for every business). Subscription = only relevant to
// businesses using recurring billing (Plans/Subscriptions tabs).
const RAZORPAY_WEBHOOK_CORE_EVENTS = [
  'payment.captured',
  'payment.authorized',
  'payment.failed',
  'order.paid',
  'payment_link.paid',
  'payment_link.expired',
  'payment_link.cancelled',
  'payment.refunded',
];

const RAZORPAY_WEBHOOK_SUBSCRIPTION_EVENTS = [
  'subscription.authenticated',
  'subscription.activated',
  'subscription.charged',
  'subscription.cancelled',
  'subscription.completed',
  'subscription.expired',
  'invoice.paid',
  'invoice.payment_failed',
];

const WebhookStepNumber: React.FC<{ n: number }> = ({ n }) => (
  <span className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-md bg-gray-50 text-gray-400 font-bold">
    {n}
  </span>
);

/**
 * Instructions for registering the WeNext webhook inside the Razorpay Dashboard.
 * Rendered in two contexts:
 *  - `pre-connect`  — below the API-credentials card, framed as "Step 2".
 *  - `post-connect` — inside the connected status view, framed as "Action
 *    required" because without the webhook, payment status never updates and
 *    payment-gated automation flows never resume.
 *
 * The Secret field is intentionally left empty: the backend verifies incoming
 * events against a platform-level secret (with a permissive no-signature
 * fallback). A business-set secret would fail that verification (HTTP 401) and
 * silently break every payment confirmation — so "leave it empty" is the only
 * instruction that works end-to-end today.
 */
const WebhookSetupCard: React.FC<{ variant: 'pre-connect' | 'post-connect' }> = ({ variant }) => {
  const [copied, setCopied] = useState<'url' | 'events' | null>(null);

  const copyValue = async (text: string, kind: 'url' | 'events') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(kind);
      setTimeout(() => setCopied((c) => (c === kind ? null : c)), 2000);
      toast({
        variant: 'success',
        title: 'Copied',
        description: kind === 'url' ? 'Webhook URL copied to clipboard' : 'Event list copied to clipboard',
      });
    } catch {
      toast({ title: 'Copy failed', description: 'Please copy it manually.', variant: 'destructive' });
    }
  };

  const allEvents = [...RAZORPAY_WEBHOOK_CORE_EVENTS, ...RAZORPAY_WEBHOOK_SUBSCRIPTION_EVENTS].join(', ');

  return (
    <div className="bg-white rounded-[12px] p-6 md:p-8 space-y-5 shadow-[0_0_10px_0_rgba(0,0,0,0.06)]">
      <div className="flex items-start justify-between gap-3 border-b border-gray-50 pb-4">
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-[#1AB169]/10 text-[#1AB169]">
            <Webhook className="h-4 w-4" />
          </div>
          <div>
            <h4 className="font-bold text-base text-[#08090A]">Set up payment confirmations</h4>
            <p className="text-xs text-gray-500 mt-0.5">Add this webhook so payment status updates automatically</p>
          </div>
        </div>
        <Badge
          variant="outline"
          className={
            variant === 'post-connect'
              ? 'bg-amber-50 text-amber-700 border-amber-200 whitespace-nowrap flex-shrink-0'
              : 'bg-gray-50 text-gray-500 border-gray-200 whitespace-nowrap flex-shrink-0'
          }
        >
          {variant === 'post-connect' ? 'Action required' : 'Step 2'}
        </Badge>
      </div>

      {variant === 'post-connect' && (
        <div className="flex gap-3 rounded-[10px] bg-amber-50/60 border border-amber-200/60 p-3.5">
          <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs leading-relaxed text-amber-800">
            Without this webhook, payments are still collected but their status won&apos;t update in WeNext and
            automation flows waiting on payment won&apos;t resume. This is a one-time setup.
          </p>
        </div>
      )}

      <ol className="space-y-4">
        <li className="flex gap-4 text-sm text-gray-600">
          <WebhookStepNumber n={1} />
          <p className="pt-0.5 leading-relaxed">
            Open your{' '}
            <a
              href="https://dashboard.razorpay.com/app/webhooks"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#1AB169] font-bold hover:underline"
            >
              Razorpay Dashboard → Webhooks
            </a>{' '}
            (Account &amp; Settings → Webhooks).
          </p>
        </li>

        <li className="flex gap-4 text-sm text-gray-600">
          <WebhookStepNumber n={2} />
          <p className="pt-0.5 leading-relaxed">
            Click <span className="font-semibold text-gray-800">+ Add New Webhook</span>.
          </p>
        </li>

        <li className="flex gap-4 text-sm text-gray-600">
          <WebhookStepNumber n={3} />
          <div className="pt-0.5 flex-1 min-w-0 space-y-2">
            <p className="leading-relaxed">
              Paste this into the <span className="font-semibold text-gray-800">Webhook URL</span> field:
            </p>
            <div className="flex items-stretch gap-2">
              <code className="flex-1 min-w-0 truncate rounded-[10px] bg-gray-50 border border-gray-200 px-3 py-2 font-mono text-xs text-gray-800">
                {RAZORPAY_WEBHOOK_URL}
              </code>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-auto rounded-[10px] flex-shrink-0"
                onClick={() => copyValue(RAZORPAY_WEBHOOK_URL, 'url')}
                title="Copy webhook URL"
              >
                {copied === 'url' ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
              </Button>
            </div>
          </div>
        </li>

        <li className="flex gap-4 text-sm text-gray-600">
          <WebhookStepNumber n={4} />
          <p className="pt-0.5 leading-relaxed">
            Leave the <span className="font-semibold text-gray-800">Secret</span> field empty — WeNext verifies every
            event automatically, so no signing secret is needed here.
          </p>
        </li>

        <li className="flex gap-4 text-sm text-gray-600">
          <WebhookStepNumber n={5} />
          <div className="pt-0.5 flex-1 min-w-0 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <p className="leading-relaxed">
                Under <span className="font-semibold text-gray-800">Active Events</span>, enable:
              </p>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs flex-shrink-0"
                onClick={() => copyValue(allEvents, 'events')}
                title="Copy all event names"
              >
                {copied === 'events' ? <Check className="h-3.5 w-3.5 mr-1" /> : <Copy className="h-3.5 w-3.5 mr-1" />}
                Copy all
              </Button>
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1.5">
                Payments &amp; links (required)
              </p>
              <div className="flex flex-wrap gap-1.5">
                {RAZORPAY_WEBHOOK_CORE_EVENTS.map((ev) => (
                  <code
                    key={ev}
                    className="rounded-md bg-[#1AB169]/5 border border-[#1AB169]/15 px-2 py-1 font-mono text-[11px] text-[#0f7a48]"
                  >
                    {ev}
                  </code>
                ))}
              </div>
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1.5">
                Subscriptions (only if you use recurring billing)
              </p>
              <div className="flex flex-wrap gap-1.5">
                {RAZORPAY_WEBHOOK_SUBSCRIPTION_EVENTS.map((ev) => (
                  <code
                    key={ev}
                    className="rounded-md bg-gray-50 border border-gray-200 px-2 py-1 font-mono text-[11px] text-gray-600"
                  >
                    {ev}
                  </code>
                ))}
              </div>
            </div>
          </div>
        </li>

        <li className="flex gap-4 text-sm text-gray-600">
          <WebhookStepNumber n={6} />
          <p className="pt-0.5 leading-relaxed">
            Click <span className="font-semibold text-gray-800">Create Webhook</span>. Payment confirmations are now
            live.
          </p>
        </li>
      </ol>
    </div>
  );
};

const RazorpayPage: React.FC<RazorpayPageProps> = ({ defaultTab = 'connect', hideTabs = false }) => {
  const { selectedAccountId, isLoading: accountsLoading } = useBusinessAccountContext();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Connection State
  const [form, setForm] = useState({
    keyId: '',
    keySecret: '',
  });
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);
  const [testing, setTesting] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionInfo, setConnectionInfo] = useState<{
    connectionType?: 'manual' | 'oauth';
    accountId?: string;
    accountName?: string;
    expiresAt?: string;
    connectedAt?: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Payments State
  const [payments, setPayments] = useState<Payment[]>([]);
  const [paymentLinks, setPaymentLinks] = useState<PaymentLink[]>([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');

  // Pagination state — initialized from the URL so it survives a refresh.
  // Keys: p=payments, l=links, pl=plans, s=subscriptions. Each tab keeps its
  // own page + size in the query string (see the URL-sync effect below).
  const readIntParam = (key: string, fallback: number) => {
    const parsed = parseInt(searchParams.get(key) || '', 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
  };
  const [paymentsPage, setPaymentsPage] = useState(() => readIntParam('ppage', 1));
  const [paymentsPageSize, setPaymentsPageSize] = useState(() => readIntParam('psize', 10));
  const [paymentsTotal, setPaymentsTotal] = useState(0);
  const [linksPage, setLinksPage] = useState(() => readIntParam('lpage', 1));
  const [linksPageSize, setLinksPageSize] = useState(() => readIntParam('lsize', 10));
  const [linksTotal, setLinksTotal] = useState(0);

  // Plans State
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [plansPage, setPlansPage] = useState(() => readIntParam('plpage', 1));
  const [plansPageSize, setPlansPageSize] = useState(() => readIntParam('plsize', 10));
  const [plansTotal, setPlansTotal] = useState(0);
  const [createPlanDialogOpen, setCreatePlanDialogOpen] = useState(false);
  const [creatingPlan, setCreatingPlan] = useState(false);
  const [planForm, setPlanForm] = useState({
    name: '',
    amount: '',
    currency: 'INR',
    description: '',
    period: 'monthly' as 'daily' | 'weekly' | 'monthly' | 'yearly',
    interval: '1',
  });

  // Subscriptions State
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  // Razorpay returns only an offer_id on the subscription; this maps id → name so
  // the Offer column can show a readable label (needs a separate Offers lookup).
  const [offersById, setOffersById] = useState<Record<string, string>>({});
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(false);
  const [subsPage, setSubsPage] = useState(() => readIntParam('spage', 1));
  const [subsPageSize, setSubsPageSize] = useState(() => readIntParam('ssize', 10));
  const [subsTotal, setSubsTotal] = useState(0);
  const [createSubDialogOpen, setCreateSubDialogOpen] = useState(false);
  const [creatingSub, setCreatingSub] = useState(false);
  const [subForm, setSubForm] = useState({
    planId: '',
    totalCount: '',
    quantity: '1',
    customerId: '',
    offerId: '',
  });

  // Create Link State
  const [createLinkDialogOpen, setCreateLinkDialogOpen] = useState(false);
  const [linkForm, setLinkForm] = useState({
    amount: '',
    currency: 'INR',
    description: '',
    customerName: '',
    customerEmail: '',
    customerContact: '',
    notifySms: true,
    notifyEmail: true,
    reminderEnable: true,
  });
  const [creatingLink, setCreatingLink] = useState(false);

  // Live amount validation for the Create Payment Link dialog. Empty field
  // shows no error (just the helper hint + disabled button); a non-empty
  // invalid value shows the inline error and blocks submit. Razorpay still
  // gates server-side, but this gives instant, actionable feedback (Razorpay's
  // own rejection never states the limit).
  const linkAmountCheck = validatePaymentAmount(linkForm.amount);
  const linkAmountError =
    linkForm.amount.trim() === '' || linkAmountCheck.ok ? null : linkAmountCheck.error;

  // URL State Management & Tab Logic
  const rawTab = searchParams.get('tab') || defaultTab;
  const activeTab = (hideTabs && !isConnected && !loadingData && !accountsLoading) ? 'connect' : rawTab;
  const setActiveTab = (tab: string) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('tab', tab);
      return newParams;
    });
  };

  useEffect(() => {
    // Clear previous BA's cached state on every switch — otherwise the UI
    // renders stale connection info / payments / plans for ~1s while the
    // new account's checkConnection is in flight.
    resetState();
    if (selectedAccountId) {
      checkConnection();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccountId]);

  // Reset pagination ONLY when switching from one real account to a different
  // one — NOT on initial mount/hydration, which would clobber the page/size we
  // just restored from the URL (the refresh-persistence bug).
  const prevAccountRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    const prev = prevAccountRef.current;
    prevAccountRef.current = selectedAccountId;
    if (prev !== undefined && prev !== null && prev !== selectedAccountId) {
      setPaymentsPageSize(10);
      setLinksPageSize(10);
      setPlansPageSize(10);
      setSubsPageSize(10);
      setPaymentsPage(1);
      setLinksPage(1);
      setPlansPage(1);
      setSubsPage(1);
    }
  }, [selectedAccountId]);

  // Mirror pagination into the URL so a refresh restores it. Default values are
  // dropped from the query string to keep the URL clean.
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        const sync = (key: string, value: number, def: number) => {
          if (value === def) next.delete(key);
          else next.set(key, String(value));
        };
        sync('ppage', paymentsPage, 1);
        sync('psize', paymentsPageSize, 10);
        sync('lpage', linksPage, 1);
        sync('lsize', linksPageSize, 10);
        sync('plpage', plansPage, 1);
        sync('plsize', plansPageSize, 10);
        sync('spage', subsPage, 1);
        sync('ssize', subsPageSize, 10);
        return next;
      },
      { replace: true }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentsPage, paymentsPageSize, linksPage, linksPageSize, plansPage, plansPageSize, subsPage, subsPageSize]);

  // Debounced search effect
  useEffect(() => {
    if (!selectedAccountId || activeTab !== 'payments') return;

    const timeoutId = setTimeout(() => {
      loadPayments();
    }, 300);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  useEffect(() => {
    if (selectedAccountId && isConnected) {
      if (activeTab === 'payments') {
        loadPayments();
      } else if (activeTab === 'links') {
        loadPaymentLinks();
      } else if (activeTab === 'plans') {
        loadPlans();
      } else if (activeTab === 'subscriptions') {
        loadSubscriptions();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccountId, activeTab, paymentsPage, paymentsPageSize, linksPage, linksPageSize, plansPage, plansPageSize, subsPage, subsPageSize, statusFilter, isConnected]);

  const resetState = () => {
    setIsConnected(false);
    setConnectionInfo(null);
    setError(null);
    setPayments([]);
    setPaymentLinks([]);
    setInitialLoadComplete(false); // Reset initial load flag
  };

  const checkConnection = async () => {
    if (!selectedAccountId) return;
    setLoadingData(true);
    setError(null);
    try {
      const res = await razorpayApi.checkConnection(selectedAccountId);
      if (res.connected) {
        setIsConnected(true);
        setConnectionInfo({
          connectionType: res.connectionType,
          accountId: res.accountId,
          accountName: res.accountName,
          expiresAt: res.expiresAt,
          connectedAt: res.connectedAt,
        });
      } else {
        setIsConnected(false);
        setConnectionInfo(null);
      }
    } catch (err: any) {
      setError(friendlyMessage(err, 'Failed to check connection'));
      setIsConnected(false);
      setConnectionInfo(null);
    } finally {
      setLoadingData(false);
    }
  };

  const handleManualConnect = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedAccountId) {
      toast({
        title: 'Error',
        description: 'Please select a business account',
        variant: 'destructive',
      });
      return;
    }

    setConnecting(true);
    setError(null);
    try {
      const result = await razorpayApi.connect({
        businessId: selectedAccountId,
        keyId: form.keyId,
        keySecret: form.keySecret,
      });

      if (result.connected) {
        toast({ variant: "success",
          title: 'Success',
          description: 'Razorpay connected successfully',
        });
        setForm({ keyId: '', keySecret: '' });
        await checkConnection();
        // Auto sync data after connection
        autoSyncData();
      }
    } catch (err: any) {
      setError(friendlyMessage(err, 'Failed to connect Razorpay'));
      showApiError(err, { fallback: { title: 'Failed to connect Razorpay' } });
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async (e?: { preventDefault?: () => void }) => {
    // Keep the confirmation dialog open (with its spinner) while the async
    // disconnect runs — Radix's AlertDialogAction would otherwise close it
    // immediately on click.
    e?.preventDefault?.();
    if (!selectedAccountId) return;

    setDisconnecting(true);
    setError(null);
    try {
      await razorpayApi.disconnect(selectedAccountId, connectionInfo?.connectionType);
      toast({ variant: "success",
        title: 'Success',
        description: 'Razorpay disconnected successfully',
      });
      setDisconnectDialogOpen(false);
      resetState();
      await checkConnection();
    } catch (err: any) {
      setError(friendlyMessage(err, 'Failed to disconnect Razorpay'));
      showApiError(err, { fallback: { title: 'Failed to disconnect Razorpay' } });
    } finally {
      setDisconnecting(false);
    }
  };

  const handleTestConnection = async () => {
    if (!selectedAccountId) return;

    setTesting(true);
    setError(null);
    try {
      const result = await razorpayApi.testConnection(selectedAccountId);
      if (result.valid) {
        toast({ variant: "success",
          title: 'Success',
          description: 'Connection test successful',
        });
      } else {
        showApiError(result, { fallback: { title: 'Connection test failed' } });
      }
    } catch (err: any) {
      showApiError(err, { fallback: { title: 'Connection test failed' } });
    } finally {
      setTesting(false);
    }
  };

  // Payment Functions
  const loadPayments = async () => {
    if (!selectedAccountId) return;
    setLoadingPayments(true);
    try {
      const result = await razorpayApi.listPayments(selectedAccountId, {
        page: paymentsPage,
        limit: paymentsPageSize,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        search: searchTerm || undefined,
      });
      setPayments(result.items || []);
      setPaymentsTotal(result.total || 0);
      setInitialLoadComplete(true); // Mark initial load as complete
    } catch (error: any) {
      showApiError(error, { fallback: { title: 'Failed to load payments' } });
      setInitialLoadComplete(true); // Mark as complete even on error
    } finally {
      setLoadingPayments(false);
    }
  };

  const handleSyncPayments = async () => {
    if (!selectedAccountId) return;
    setLoadingPayments(true);
    try {
      const result = await razorpayApi.syncPayments(selectedAccountId, { count: 100 });
      const count = result.synced || 0;
      const errSuffix = result.errors > 0 ? ` (${result.errors} errors)` : '';
      toast(count > 0
        ? { title: 'Payments synced', description: `Synced ${count} ${count === 1 ? 'payment' : 'payments'} from Razorpay${errSuffix}.` }
        : { title: 'Up to date', description: `No new payments to sync from Razorpay${errSuffix}.` }
      );
      await loadPayments();
    } catch (error: any) {
      showApiError(error, { fallback: { title: 'Failed to sync payments' } });
    } finally {
      setLoadingPayments(false);
    }
  };

  const handleSyncPaymentLinks = async () => {
    if (!selectedAccountId) return;
    setLoadingPayments(true);
    try {
      const result = await razorpayApi.syncPaymentLinks(selectedAccountId, {
        count: 100,
        skip: 0,
      });
      const count = result.synced || 0;
      const errSuffix = result.errors > 0 ? ` (${result.errors} errors)` : '';
      toast(count > 0
        ? { title: 'Payment links synced', description: `Synced ${count} ${count === 1 ? 'link' : 'links'} from Razorpay${errSuffix}.` }
        : { title: 'Up to date', description: `No new payment links to sync from Razorpay${errSuffix}.` }
      );
      await loadPaymentLinks();
    } catch (error: any) {
      showApiError(error, { fallback: { title: 'Failed to sync payment links' } });
    } finally {
      setLoadingPayments(false);
    }
  };

  const loadPaymentLinks = async () => {
    if (!selectedAccountId) return;
    setLoadingPayments(true);
    try {
      // Razorpay's /payment_links list endpoint does NOT support page/limit —
      // it returns the full set in one shot. We hold the complete list and
      // paginate client-side (see `paginatedLinks`) so the chosen page size is
      // actually honored instead of rendering Razorpay's raw ~25-item default.
      const result = await razorpayApi.listPaymentLinks(selectedAccountId, {});
      // Razorpay API returns payment_links array, not items
      const all = result.payment_links || result.items || [];
      setPaymentLinks(all);
      setLinksTotal(all.length);
    } catch (error: any) {
      showApiError(error, { fallback: { title: 'Failed to load payment links' } });
    } finally {
      setLoadingPayments(false);
    }
  };

  const autoSyncData = async () => {
    if (!selectedAccountId) return;

    // Don't set loading state here as we want this to happen in background
    // or we can show a toast that sync is starting
    toast({
      title: 'Syncing Data',
      description: 'Syncing payments and payment links...',
    });

    try {
      // Sync payments
      await razorpayApi.syncPayments(selectedAccountId, { count: 100 });
      // Sync payment links
      await razorpayApi.syncPaymentLinks(selectedAccountId, { count: 100, skip: 0 });

      // Reload data
      await Promise.all([
        loadPayments(),
        loadPaymentLinks()
      ]);

      toast({ variant: "success",
        title: 'Sync Complete',
        description: 'Payments and payment links have been synced successfully.',
      });
    } catch (error: any) {
      toast({
        title: 'Sync Warning',
        description: 'Connected successfully but failed to sync some data. You can try syncing manually.',
        variant: 'destructive',
      });
    }
  };

  const handleCreatePaymentLink = async () => {
    if (!selectedAccountId) return;

    const amountCheck = validatePaymentAmount(linkForm.amount);
    if (!amountCheck.ok) {
      toast({
        title: 'Invalid amount',
        description: amountCheck.error,
        variant: 'destructive',
      });
      return;
    }
    const amountNum = amountCheck.value;

    setCreatingLink(true);
    try {
      const payload: any = {
        amount: amountNum,
        currency: linkForm.currency,
        description: linkForm.description || 'Payment Request',
        notify: {
          sms: linkForm.notifySms,
          email: linkForm.notifyEmail,
        },
        reminder_enable: linkForm.reminderEnable,
      };

      if (linkForm.customerName || linkForm.customerEmail || linkForm.customerContact) {
        payload.customer = {};
        if (linkForm.customerName) payload.customer.name = linkForm.customerName;
        if (linkForm.customerEmail) payload.customer.email = linkForm.customerEmail;
        if (linkForm.customerContact) payload.customer.contact = linkForm.customerContact;
      }

      const result = await razorpayApi.createPaymentLink(selectedAccountId, payload);

      // Backend returns {success: true, link: {...}}
      if (result.link || result.paymentLink) {
        toast({ variant: "success",
          title: 'Success',
          description: 'Payment link created successfully',
        });
        setCreateLinkDialogOpen(false);
        setLinkForm({
          amount: '',
          currency: 'INR',
          description: '',
          customerName: '',
          customerEmail: '',
          customerContact: '',
          notifySms: true,
          notifyEmail: true,
          reminderEnable: true,
        });
        await loadPaymentLinks();
      }
    } catch (error: any) {
      showApiError(error, { fallback: { title: 'Failed to create payment link' } });
    } finally {
      setCreatingLink(false);
    }
  };

  const handleCopyLink = (url: string) => {
    navigator.clipboard.writeText(url);
    toast({ variant: "success",
      title: 'Copied',
      description: 'Payment link copied to clipboard',
    });
  };

  // Plan Functions
  const loadPlans = async () => {
    if (!selectedAccountId) return;
    setLoadingPlans(true);
    try {
      const skip = (plansPage - 1) * plansPageSize;
      // Fetch one extra to detect if there's a next page
      const result = await razorpayApi.listPlans(selectedAccountId, { count: plansPageSize + 1, skip });
      const items = result.items || [];
      const hasMore = items.length > plansPageSize;
      setPlans(hasMore ? items.slice(0, plansPageSize) : items);
      // Estimate total: current offset + items fetched (+ 1 if there's more)
      setPlansTotal(hasMore ? skip + plansPageSize + 1 : skip + items.length);
    } catch (error: any) {
      showApiError(error, { fallback: { title: 'Failed to load plans' } });
    } finally {
      setLoadingPlans(false);
    }
  };

  const handleCreatePlan = async () => {
    if (!selectedAccountId) return;

    const amountNum = parseFloat(planForm.amount);
    if (!planForm.name || !planForm.amount || isNaN(amountNum) || amountNum <= 0) {
      toast({
        title: 'Error',
        description: 'Please enter a valid name and amount',
        variant: 'destructive',
      });
      return;
    }

    setCreatingPlan(true);
    try {
      await razorpayApi.createPlan(selectedAccountId, {
        period: planForm.period,
        interval: parseInt(planForm.interval) || 1,
        item: {
          name: planForm.name,
          amount: amountNum,
          currency: planForm.currency,
          description: planForm.description || undefined,
        },
      });

      toast({ variant: "success",
        title: 'Success',
        description: 'Plan created successfully',
      });
      setCreatePlanDialogOpen(false);
      setPlanForm({
        name: '',
        amount: '',
        currency: 'INR',
        description: '',
        period: 'monthly',
        interval: '1',
      });
      await loadPlans();
    } catch (error: any) {
      showApiError(error, { fallback: { title: 'Failed to create plan' } });
    } finally {
      setCreatingPlan(false);
    }
  };

  // Subscription Functions
  const loadSubscriptions = async () => {
    if (!selectedAccountId) return;
    setLoadingSubscriptions(true);
    try {
      const skip = (subsPage - 1) * subsPageSize;
      // Fetch one extra to detect if there's a next page
      const result = await razorpayApi.listSubscriptions(selectedAccountId, { count: subsPageSize + 1, skip });
      const items = result.items || [];
      const hasMore = items.length > subsPageSize;
      setSubscriptions(hasMore ? items.slice(0, subsPageSize) : items);
      setSubsTotal(hasMore ? skip + subsPageSize + 1 : skip + items.length);

      // Best-effort: resolve offer names for the Offer column. Non-fatal — if
      // offers can't be fetched, the column falls back to the raw offer id.
      try {
        const offersRes: any = await razorpayApi.listOffers(selectedAccountId);
        const offerItems = Array.isArray(offersRes?.items) ? offersRes.items : [];
        const map: Record<string, string> = {};
        for (const o of offerItems) {
          if (o?.id) map[o.id] = o.name || o.id;
        }
        setOffersById(map);
      } catch {
        /* offers are optional — leave the map as-is */
      }
    } catch (error: any) {
      showApiError(error, { fallback: { title: 'Failed to load subscriptions' } });
    } finally {
      setLoadingSubscriptions(false);
    }
  };

  const handleCreateSubscription = async () => {
    if (!selectedAccountId) return;

    if (!subForm.planId || !subForm.totalCount) {
      toast({
        title: 'Error',
        description: 'Please select a plan and enter total count',
        variant: 'destructive',
      });
      return;
    }

    setCreatingSub(true);
    try {
      await razorpayApi.createSubscription(selectedAccountId, {
        planId: subForm.planId,
        totalCount: parseInt(subForm.totalCount),
        quantity: parseInt(subForm.quantity) || 1,
        customerId: subForm.customerId || undefined,
        offerId: subForm.offerId.trim() ? subForm.offerId.trim() : undefined,
      });

      toast({ variant: "success",
        title: 'Success',
        description: 'Subscription created successfully',
      });
      setCreateSubDialogOpen(false);
      setSubForm({
        planId: '',
        totalCount: '',
        quantity: '1',
        customerId: '',
        offerId: 'none',
      });
      await loadSubscriptions();
    } catch (error: any) {
      showApiError(error, { fallback: { title: 'Failed to create subscription' } });
    } finally {
      setCreatingSub(false);
    }
  };

  const handleCancelSubscription = async (subscriptionId: string) => {
    if (!selectedAccountId) return;
    try {
      await razorpayApi.cancelSubscription(selectedAccountId, subscriptionId, false);
      toast({ variant: "success", title: 'Success', description: 'Subscription cancelled' });
      await loadSubscriptions();
    } catch (error: any) {
      showApiError(error, { fallback: { title: 'Failed to cancel subscription' } });
    }
  };

  const handlePauseSubscription = async (subscriptionId: string) => {
    if (!selectedAccountId) return;
    try {
      await razorpayApi.pauseSubscription(selectedAccountId, subscriptionId);
      toast({ variant: "success", title: 'Success', description: 'Subscription paused' });
      await loadSubscriptions();
    } catch (error: any) {
      showApiError(error, { fallback: { title: 'Failed to pause subscription' } });
    }
  };

  const handleResumeSubscription = async (subscriptionId: string) => {
    if (!selectedAccountId) return;
    try {
      await razorpayApi.resumeSubscription(selectedAccountId, subscriptionId);
      toast({ variant: "success", title: 'Success', description: 'Subscription resumed' });
      await loadSubscriptions();
    } catch (error: any) {
      showApiError(error, { fallback: { title: 'Failed to resume subscription' } });
    }
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline'; label: string; icon: any; style?: React.CSSProperties }> = {
      captured: { variant: 'default', label: 'Paid', icon: CheckCircle, style: { backgroundColor: 'var(--secondary-color)', color: '#fff' } },
      authorized: { variant: 'secondary', label: 'Authorized', icon: Clock },
      failed: { variant: 'destructive', label: 'Failed', icon: XCircle },
      pending: { variant: 'outline', label: 'Pending', icon: Clock },
      created: { variant: 'outline', label: 'Created', icon: Clock },
      paid: { variant: 'default', label: 'Paid', icon: CheckCircle, style: { backgroundColor: 'var(--secondary-color)', color: '#fff' } },
      active: { variant: 'default', label: 'Active', icon: CheckCircle },
      expired: { variant: 'destructive', label: 'Expired', icon: XCircle },
      cancelled: { variant: 'destructive', label: 'Cancelled', icon: XCircle },
      refunded: { variant: 'outline', label: 'Refunded', icon: RefreshCw, style: { backgroundColor: '#FEF3C7', color: '#92400E', borderColor: '#FCD34D' } },
      authenticated: { variant: 'secondary', label: 'Authenticated', icon: CheckCircle },
      halted: { variant: 'destructive', label: 'Halted', icon: XCircle },
      paused: { variant: 'outline', label: 'Paused', icon: Pause, style: { backgroundColor: '#FEF3C7', color: '#92400E', borderColor: '#FCD34D' } },
      completed: { variant: 'default', label: 'Completed', icon: CheckCircle, style: { backgroundColor: 'var(--secondary-color)', color: '#fff' } },
    };

    const statusInfo = statusMap[status.toLowerCase()] || { variant: 'outline' as const, label: status, icon: Clock };
    const Icon = statusInfo.icon;

    return (
      <Badge variant={statusInfo.variant} className="flex items-center w-fit gap-1" style={statusInfo.style}>
        <Icon className="h-3 w-3" />
        {statusInfo.label}
      </Badge>
    );
  };

  const paymentsTotalPages = Math.ceil(paymentsTotal / paymentsPageSize);
  const linksTotalPages = Math.ceil(linksTotal / linksPageSize);
  const plansTotalPages = Math.ceil(plansTotal / plansPageSize);
  const subsTotalPages = Math.ceil(subsTotal / subsPageSize);

  // Payment links are fetched in full from Razorpay (no server pagination), so
  // slice the current page client-side. Clamp the page if the size changed and
  // pushed the current page out of range.
  const paginatedLinks = useMemo(() => {
    const start = (linksPage - 1) * linksPageSize;
    return paymentLinks.slice(start, start + linksPageSize);
  }, [paymentLinks, linksPage, linksPageSize]);

  // If a restored/stale links page is now beyond the available pages, snap back.
  useEffect(() => {
    if (linksTotalPages > 0 && linksPage > linksTotalPages) {
      setLinksPage(linksTotalPages);
    }
  }, [linksTotalPages, linksPage]);

  const paymentsColumns: Column<Payment>[] = useMemo(() => [
    {
      key: 'razorpay_payment_id',
      header: 'Payment ID',
      render: (_, payment) => (
        <div className="font-mono text-xs text-foreground">
          {payment.razorpay_payment_id?.substring(0, 16) || payment.id?.substring(0, 16) || 'N/A'}
        </div>
      ),
    },
    {
      key: 'amount',
      header: 'Amount',
      render: (_, payment) => (
        <div className="font-semibold text-base">
          {new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: payment.currency || 'INR',
          }).format((payment.amount || 0) / 100)}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (_, payment) => getStatusBadge(payment.status),
    },
    {
      key: 'payment_method',
      header: 'Method',
      render: (_, payment) => (
        <span className="text-sm text-muted-foreground capitalize">
          {payment.payment_method || payment.method || 'N/A'}
        </span>
      ),
    },
    {
      key: 'created_at',
      header: 'Date',
      render: (_, payment) => (
        <span className="text-sm text-muted-foreground">
          {(() => {
            const dateValue = payment.created_at;
            if (!dateValue) return 'N/A';
            const date = typeof dateValue === 'number'
              ? new Date(dateValue * 1000)
              : new Date(dateValue);
            return date.toLocaleString('en-IN', {
              dateStyle: 'medium',
              timeStyle: 'short',
            });
          })()}
        </span>
      ),
    },
  ], []);

  const paymentLinksColumns: Column<PaymentLink>[] = useMemo(() => [
    {
      key: 'short_url',
      header: 'Payment Link',
      render: (_, link) => (
        <div className="flex items-center gap-2">
          <a
            href={link.short_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline text-sm font-medium flex items-center gap-1.5 transition-colors"
          >
            {link.short_url}
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 hover:bg-muted"
            onClick={() => handleCopyLink(link.short_url)}
            title="Copy link"
          >
            <Copy className="h-3.5 w-3.5" />
          </Button>
        </div>
      ),
    },
    {
      key: 'amount',
      header: 'Amount',
      render: (_, link) => (
        <div className="font-semibold text-base">
          {new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: link.currency || 'INR',
          }).format((link.amount || 0) / 100)}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (_, link) => getStatusBadge(link.status),
    },
    {
      key: 'amount_paid',
      header: 'Paid',
      render: (_, link) => (
        <div className="text-sm">
          {link.amount_paid !== undefined && link.amount_paid > 0 ? (
            <span className="text-green-600 font-medium">
              {new Intl.NumberFormat('en-IN', {
                style: 'currency',
                currency: link.currency || 'INR',
              }).format((link.amount_paid || 0) / 100)}
            </span>
          ) : (
            <span className="text-muted-foreground">-</span>
          )}
        </div>
      ),
    },
    {
      key: 'description',
      header: 'Description',
      render: (_, link) => (
        <span className="text-sm text-muted-foreground line-clamp-1 max-w-[200px]">
          {link.description || 'N/A'}
        </span>
      ),
    },
    {
      key: 'created_at',
      header: 'Created',
      render: (_, link) => (
        <span className="text-sm text-muted-foreground">
          {(() => {
            const dateValue = link.created_at || link.updated_at;
            if (!dateValue) return 'N/A';
            const date = typeof dateValue === 'number'
              ? new Date(dateValue * 1000)
              : new Date(dateValue);
            return date.toLocaleString('en-IN', {
              dateStyle: 'medium',
              timeStyle: 'short',
            });
          })()}
        </span>
      ),
    },
  ], []);

  const plansColumns: Column<Plan>[] = useMemo(() => [
    {
      key: 'id',
      header: 'Plan ID',
      render: (_, plan) => (
        <div className="font-mono text-xs text-foreground">
          {plan.id?.substring(0, 20) || 'N/A'}
        </div>
      ),
    },
    {
      key: 'item',
      header: 'Name',
      render: (_, plan) => (
        <div>
          <div className="font-semibold">{plan.item?.name || 'N/A'}</div>
          {plan.item?.description && (
            <div className="text-xs text-muted-foreground line-clamp-1">{plan.item.description}</div>
          )}
        </div>
      ),
    },
    {
      key: 'amount',
      header: 'Amount',
      render: (_, plan) => (
        <div className="font-semibold text-base">
          {new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: plan.item?.currency || 'INR',
          }).format((plan.item?.amount || 0) / 100)}
        </div>
      ),
    },
    {
      key: 'period',
      header: 'Billing Cycle',
      render: (_, plan) => (
        <span className="text-sm capitalize">
          Every {plan.interval > 1 ? `${plan.interval} ` : ''}{plan.period}{plan.interval > 1 ? 's' : ''}
        </span>
      ),
    },
    {
      key: 'created_at',
      header: 'Created',
      render: (_, plan) => (
        <span className="text-sm text-muted-foreground">
          {plan.created_at ? new Date(plan.created_at * 1000).toLocaleString('en-IN', {
            dateStyle: 'medium',
            timeStyle: 'short',
          }) : 'N/A'}
        </span>
      ),
    },
  ], []);

  const subscriptionsColumns: Column<Subscription>[] = useMemo(() => [
    {
      key: 'id',
      header: 'Subscription ID',
      render: (_, sub) => (
        <div className="font-mono text-xs text-foreground">
          {sub.id?.substring(0, 20) || 'N/A'}
        </div>
      ),
    },
    {
      key: 'plan_id',
      header: 'Plan',
      render: (_, sub) => (
        <div className="font-mono text-xs text-muted-foreground">
          {sub.plan_id?.substring(0, 20) || 'N/A'}
        </div>
      ),
    },
    {
      key: 'offer_id',
      header: 'Offer',
      render: (_, sub) =>
        sub.offer_id ? (
          <span className="text-sm" title={sub.offer_id}>
            {offersById[sub.offer_id] || sub.offer_id}
          </span>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (_, sub) => getStatusBadge(sub.status),
    },
    {
      key: 'paid_count',
      header: 'Progress',
      render: (_, sub) => (
        <span className="text-sm">
          {sub.paid_count ?? 0} / {sub.total_count ?? '∞'} cycles
        </span>
      ),
    },
    {
      key: 'current_end',
      header: 'Current Period End',
      render: (_, sub) => (
        <span className="text-sm text-muted-foreground">
          {sub.current_end ? new Date(sub.current_end * 1000).toLocaleString('en-IN', {
            dateStyle: 'medium',
            timeStyle: 'short',
          }) : 'N/A'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (_, sub) => (
        <div className="flex items-center justify-end gap-1">
          {sub.status === 'active' && (
            <button
              className="inline-flex items-center h-6 px-2 rounded text-[11px] font-medium text-yellow-700 hover:bg-yellow-50 transition-colors"
              onClick={() => handlePauseSubscription(sub.id)}
            >
              <Pause className="h-3 w-3 mr-1" />
              Pause
            </button>
          )}
          {sub.status === 'paused' && (
            <button
              className="inline-flex items-center h-6 px-2 rounded text-[11px] font-medium text-green-700 hover:bg-green-50 transition-colors"
              onClick={() => handleResumeSubscription(sub.id)}
            >
              <Play className="h-3 w-3 mr-1" />
              Resume
            </button>
          )}
          {['active', 'authenticated', 'created'].includes(sub.status) && (
            <button
              className="inline-flex items-center h-6 px-2 rounded text-[11px] font-medium text-red-500 hover:bg-red-50 transition-colors"
              onClick={() => handleCancelSubscription(sub.id)}
            >
              <Ban className="h-3 w-3 mr-1" />
              Cancel
            </button>
          )}
          {['cancelled', 'completed', 'expired'].includes(sub.status) && (
            <span className="text-[11px] text-muted-foreground">—</span>
          )}
        </div>
      ),
    },
  ], []);

  if (accountsLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className=" space-y-6">
      {!hideTabs && (
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/home/integrations')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <CardTitle className="text-xl sm:text-2xl">Razorpay Integration</CardTitle>
            <CardDescription className="text-sm">Connect your Razorpay account to process payments</CardDescription>
          </div>
        </div>
      )}

      {error && (
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <p className="text-sm text-destructive">{error}</p>
          </CardContent>
        </Card>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        {!hideTabs && (
          <TabsList className='inline-flex gap-2 p-2 h-[46px] rounded-[20px]' >
            <TabsTrigger className='relative rounded-[12px] data-[state=active]:bg-transparent data-[state=active]:shadow-none' value="connect">
              <span className="relative z-10">Connect</span>
              {activeTab === 'connect' && (
                <motion.div
                  layoutId="razorpay-tab-pill"
                  className="absolute inset-0 bg-background rounded-[12px] shadow-sm z-0"
                  transition={{ type: "spring", bounce: 0.2, duration: 0.5 }}
                />
              )}
            </TabsTrigger>
            <TabsTrigger className='relative rounded-[12px] data-[state=active]:bg-transparent data-[state=active]:shadow-none' value="payments"
              disabled={!isConnected}>
              <span className="relative z-10">Payments</span>
              {activeTab === 'payments' && (
                <motion.div
                  layoutId="razorpay-tab-pill"
                  className="absolute inset-0 bg-background rounded-[12px] shadow-sm z-0"
                  transition={{ type: "spring", bounce: 0.2, duration: 0.5 }}
                />
              )}
            </TabsTrigger>
            <TabsTrigger className='relative rounded-[12px] data-[state=active]:bg-transparent data-[state=active]:shadow-none' value="links"
              disabled={!isConnected}>
              <span className="relative z-10">Payment Links</span>
              {activeTab === 'links' && (
                <motion.div
                  layoutId="razorpay-tab-pill"
                  className="absolute inset-0 bg-background rounded-[12px] shadow-sm z-0"
                  transition={{ type: "spring", bounce: 0.2, duration: 0.5 }}
                />
              )}
            </TabsTrigger>
            <TabsTrigger className='relative rounded-[12px] data-[state=active]:bg-transparent data-[state=active]:shadow-none' value="plans"
              disabled={!isConnected}>
              <span className="relative z-10">Plans</span>
              {activeTab === 'plans' && (
                <motion.div
                  layoutId="razorpay-tab-pill"
                  className="absolute inset-0 bg-background rounded-[12px] shadow-sm z-0"
                  transition={{ type: "spring", bounce: 0.2, duration: 0.5 }}
                />
              )}
            </TabsTrigger>
            <TabsTrigger className='relative rounded-[12px] data-[state=active]:bg-transparent data-[state=active]:shadow-none' value="subscriptions"
              disabled={!isConnected}>
              <span className="relative z-10">Subscriptions</span>
              {activeTab === 'subscriptions' && (
                <motion.div
                  layoutId="razorpay-tab-pill"
                  className="absolute inset-0 bg-background rounded-[12px] shadow-sm z-0"
                  transition={{ type: "spring", bounce: 0.2, duration: 0.5 }}
                />
              )}
            </TabsTrigger>
          </TabsList>
        )}
        <TabsContent value="connect" className="">
          <Card className='border-none'>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>{isConnected ? 'Connection Status' : 'Connect Razorpay'}</CardTitle>
                  <CardDescription>
                    {isConnected
                      ? 'Current Razorpay connection status'
                      : 'Enter your Razorpay API credentials'}
                  </CardDescription>
                </div>
                {isConnected && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={checkConnection}
                    disabled={loadingData || !selectedAccountId}
                  >
                    <RefreshCw className={`h-4 w-4 mr-2 ${loadingData ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {loadingData ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : isConnected ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                      <PlugZap className="h-3 w-3 mr-1" />
                      Connected
                    </Badge>
                    <Badge variant="outline">
                      {connectionInfo?.connectionType === 'oauth' ? 'OAuth' : 'Manual'}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-muted-foreground">Account ID</Label>
                      <p className="font-medium">{connectionInfo?.accountId || 'N/A'}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Account Name</Label>
                      <p className="font-medium">{connectionInfo?.accountName || 'N/A'}</p>
                    </div>
                    {connectionInfo?.expiresAt && (
                      <div>
                        <Label className="text-muted-foreground">Token Expires</Label>
                        <p className="font-medium">
                          {new Date(connectionInfo.expiresAt).toLocaleString()}
                        </p>
                      </div>
                    )}
                    {connectionInfo?.connectedAt && (
                      <div>
                        <Label className="text-muted-foreground">Connected At</Label>
                        <p className="font-medium">
                          {new Date(connectionInfo.connectedAt).toLocaleString()}
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={handleTestConnection}
                      disabled={testing}
                    >
                      {testing ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <RefreshCw className="h-4 w-4 mr-2" />
                      )}
                      Test Connection
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={() => setDisconnectDialogOpen(true)}
                      disabled={disconnecting}
                    >
                      {disconnecting ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Unplug className="h-4 w-4 mr-2" />
                      )}
                      Disconnect
                    </Button>
                  </div>

                  <WebhookSetupCard variant="post-connect" />

                  <DisconnectConfirmDialog
                    open={disconnectDialogOpen}
                    onOpenChange={setDisconnectDialogOpen}
                    onConfirm={handleDisconnect}
                    isLoading={disconnecting}
                    integration="razorpay"
                  />
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-4 md:py-10">
                  <div className="w-full space-y-8">
                    {/* Header Section */}
                    <div className="space-y-4 text-center">
                      <div className="flex items-center justify-center">
                        <div className="p-4 bg-[#1AB169]/10 rounded-2xl border border-[#1AB169]/20 shadow-sm shadow-[#1AB169]/10">
                          <Key className="h-8 w-8 text-[#1AB169]" />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <h3 className="text-2xl md:text-4xl font-extrabold tracking-tight text-[#08090A]">
                          Connect Razorpay
                        </h3>
                        <p className="text-gray-500 text-sm md:text-base max-w-sm mx-auto leading-relaxed">
                          Securely accept payments via Credit Cards, UPI, and Netbanking in your store.
                        </p>
                      </div>
                    </div>

                    {/* Connection Form */}
                    <Card className="border-none shadow-[0_0_10px_0_rgba(0,0,0,0.06)] rounded-[12px]">
                      <CardContent className="p-6 md:p-8">
                        <form onSubmit={handleManualConnect} className="space-y-6">
                          <div className="space-y-5">
                            <div className="space-y-2.5">
                              <Label htmlFor="keyId" className="text-sm font-semibold text-gray-700 ml-1">Key ID</Label>
                              <Input
                                id="keyId"
                                type="text"
                                placeholder="rzp_test_..."
                                value={form.keyId}
                                onChange={(e) => setForm({ ...form, keyId: e.target.value })}
                                required
                                className="h-12 rounded-[12px] bg-gray-50/50 border-gray-200 focus:bg-white focus:border-[#1AB169] focus:ring-[#1AB169]/10 transition-all font-mono"
                              />
                            </div>
                            <div className="space-y-2.5">
                              <Label htmlFor="keySecret" className="text-sm font-semibold text-gray-700 ml-1">Key Secret</Label>
                              <Input
                                id="keySecret"
                                type="password"
                                placeholder="••••••••••••••••"
                                value={form.keySecret}
                                onChange={(e) => setForm({ ...form, keySecret: e.target.value })}
                                required
                                className="h-12 rounded-[12px] bg-gray-50/50 border-gray-200 focus:bg-white focus:border-[#1AB169] focus:ring-[#1AB169]/10 transition-all"
                              />
                            </div>
                          </div>

                          <Button
                            type="submit"
                            className="w-[fit] h-12 bg-[#1AB169] hover:bg-[#159a5b] text-white rounded-[12px] font-bold text-base shadow-lg shadow-[#1AB169]/20 transition-all active:scale-[0.98] disabled:opacity-70"
                            disabled={connecting || !selectedAccountId}
                          >
                            {connecting ? (
                              <>
                                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                                Connecting to Razorpay...
                              </>
                            ) : (
                              <>
                                <PlugZap className="h-5 w-5 mr-2" />
                                Connect Account
                              </>
                            )}
                          </Button>
                        </form>
                      </CardContent>
                    </Card>

                    {/* Info Section */}
                    <div className="bg-white rounded-[12px] p-6 md:p-8 space-y-5 shadow-[0_0_10px_0_rgba(0,0,0,0.06)]">
                      <div className="flex items-center gap-3 border-b border-gray-50 pb-4">
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#1AB169]/10 text-[#1AB169]">
                          <span className="font-bold text-sm">i</span>
                        </div>
                        <h4 className="font-bold text-base text-[#08090A]">
                          How to find your API credentials
                        </h4>
                      </div>

                      <ol className="space-y-4">
                        {[
                          { text: "Log in to your", link: "Razorpay Dashboard", url: "https://dashboard.razorpay.com" },
                          { text: "Navigate to Settings → API Keys sidebar menu" },
                          { text: "Click on Generate Key or Regenerate Key" },
                          { text: "Copy the Key ID and Key Secret and paste them above" }
                        ].map((step, idx) => (
                          <li key={idx} className="flex gap-4 text-sm text-gray-600 group">
                            <span className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-md bg-gray-50 text-gray-400 font-bold group-hover:bg-[#1AB169]/5 group-hover:text-[#1AB169] transition-colors">
                              {idx + 1}
                            </span>
                            <p className="pt-0.5 leading-relaxed">
                              {step.text}{" "}
                              {step.link && (
                                <a href={step.url} target="_blank" rel="noopener noreferrer" className="text-[#1AB169] font-bold hover:underline">
                                  {step.link}
                                </a>
                              )}
                            </p>
                          </li>
                        ))}
                      </ol>
                    </div>

                    {/* Webhook setup — payment confirmations. Shown before connecting too,
                        framed as Step 2, so the business can configure both in one visit. */}
                    <WebhookSetupCard variant="pre-connect" />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="payments" className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className='text-lg sm:text-xl md:text-2xl font-bold' >Payment History</CardTitle>
              <CardDescription className='text-xs sm:text-sm md:text-base text-gray-600' >All payments processed through Razorpay</CardDescription>
            </div>
          </div>
          <div className='flex justify-between flex-wrap gap-4 pb-[0px] ' >
            <SearchBar
              placeholder="Search by Payment ID, Customer Name, Email, or Phone..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPaymentsPage(1);
              }}
              onClear={() => {
                setSearchTerm('');
                setPaymentsPage(1);
              }}
              containerClassName="max-w-sm"
            />

            <Select value={statusFilter} onValueChange={(value) => {
              setStatusFilter(value);
              setPaymentsPage(1);
            }}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="captured">Paid</SelectItem>
                <SelectItem value="authorized">Authorized</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="refunded">Refunded</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Card className='rounded-[12px] border-0 shadow-[0px_0px_10px_0px_rgba(0,0,0,0.06)] ' >
            <CardContent>
              <div className="space-y-4">
                {loadingPayments || loadingData || !initialLoadComplete ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin" />
                  </div>
                ) : payments.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <CreditCard className="h-12 w-12 text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No payments found</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      {searchTerm || statusFilter !== 'all'
                        ? 'Try adjusting your search or filters'
                        : 'Sync payments from Razorpay to get started'}
                    </p>
                    {!searchTerm && statusFilter === 'all' && (
                      <Button
                        variant="outline"
                        onClick={handleSyncPayments}
                        disabled={loadingPayments || !selectedAccountId}
                      >
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Sync
                      </Button>
                    )}
                  </div>
                ) : (
                  <>
                    <DataTable
                      data={payments}
                      columns={paymentsColumns}
                      showPagination={false}
                      innerScroll={false}
                    />
                    {paymentsTotalPages > 1 && (
                      <ContactsPagination
                        currentPage={paymentsPage}
                        totalPages={paymentsTotalPages}
                        totalCount={paymentsTotal}
                        pageSize={paymentsPageSize}
                        onPageChange={setPaymentsPage}
                        onPageSizeChange={(size) => {
                          setPaymentsPageSize(size);
                          setPaymentsPage(1);
                        }}
                        isLoading={loadingPayments}
                        itemLabel="payments"
                        pageSizeOptions={[10, 20]}
                        minPageSize={10}
                      />
                    )}
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="links" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Payment Links</CardTitle>
                  <CardDescription>Payment links created for customers</CardDescription>
                </div>
                {paymentLinks.length > 0 && (
                  <Button
                    onClick={() => setCreateLinkDialogOpen(true)}
                    disabled={!selectedAccountId}
                    size="sm"
                  >
                    <LinkIcon className="h-4 w-4 mr-2" />
                    Create Link
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {loadingPayments ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : paymentLinks.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <LinkIcon className="h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No payment links found</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Create your first payment link to start accepting payments
                  </p>
                  <Button
                    onClick={() => setCreateLinkDialogOpen(true)}
                    disabled={!selectedAccountId}
                  >
                    <LinkIcon className="h-4 w-4 mr-2" />
                    Create Payment Link
                  </Button>
                </div>
              ) : (
                <>
                  <DataTable
                    data={paginatedLinks}
                    columns={paymentLinksColumns}
                    showPagination={false}
                    innerScroll={false}
                  />
                  {linksTotalPages > 1 && (
                    <ContactsPagination
                      currentPage={linksPage}
                      totalPages={linksTotalPages}
                      totalCount={linksTotal}
                      pageSize={linksPageSize}
                      onPageChange={setLinksPage}
                      onPageSizeChange={(size) => {
                        setLinksPageSize(size);
                        setLinksPage(1);
                      }}
                      isLoading={loadingPayments}
                      itemLabel="payment links"
                      pageSizeOptions={[10, 20]}
                      minPageSize={10}
                    />
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Plans Tab */}
        <TabsContent value="plans" className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className='text-lg sm:text-xl md:text-2xl font-bold'>Plans</CardTitle>
              <CardDescription className='text-xs sm:text-sm md:text-base text-gray-600'>Manage recurring billing plans</CardDescription>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={loadPlans}
                disabled={loadingPlans || !selectedAccountId}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loadingPlans ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button
                size="sm"
                onClick={() => setCreatePlanDialogOpen(true)}
                disabled={!selectedAccountId}
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Plan
              </Button>
            </div>
          </div>
          <Card className='rounded-[12px] border-0 shadow-[0px_0px_10px_0px_rgba(0,0,0,0.06)]'>
            <CardContent>
              <div className="space-y-4">
                {loadingPlans ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin" />
                  </div>
                ) : plans.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <Package className="h-12 w-12 text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No plans found</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Create your first plan to start accepting recurring payments
                    </p>
                    <Button
                      onClick={() => setCreatePlanDialogOpen(true)}
                      disabled={!selectedAccountId}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Create Plan
                    </Button>
                  </div>
                ) : (
                  <>
                    <DataTable
                      data={plans}
                      columns={plansColumns}
                      showPagination={false}
                      innerScroll={false}
                    />
                    <ContactsPagination
                      currentPage={plansPage}
                      totalPages={plansTotalPages}
                      totalCount={plansTotal}
                      pageSize={plansPageSize}
                      onPageChange={setPlansPage}
                      onPageSizeChange={(size) => {
                        setPlansPageSize(size);
                        setPlansPage(1);
                      }}
                      isLoading={loadingPlans}
                      itemLabel="plans"
                      pageSizeOptions={[10, 20]}
                      minPageSize={10}
                      hideTotalCount
                    />
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Subscriptions Tab */}
        <TabsContent value="subscriptions" className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className='text-lg sm:text-xl md:text-2xl font-bold'>Subscriptions</CardTitle>
              <CardDescription className='text-xs sm:text-sm md:text-base text-gray-600'>Manage customer subscriptions</CardDescription>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={loadSubscriptions}
                disabled={loadingSubscriptions || !selectedAccountId}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loadingSubscriptions ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  if (plans.length === 0) loadPlans();
                  setCreateSubDialogOpen(true);
                }}
                disabled={!selectedAccountId}
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Subscription
              </Button>
            </div>
          </div>
          <Card className='rounded-[12px] border-0 shadow-[0px_0px_10px_0px_rgba(0,0,0,0.06)]'>
            <CardContent>
              <div className="space-y-4">
                {loadingSubscriptions ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin" />
                  </div>
                ) : subscriptions.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <CalendarClock className="h-12 w-12 text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No subscriptions found</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Create a subscription to start billing customers on a recurring basis
                    </p>
                    <Button
                      onClick={() => {
                        if (plans.length === 0) loadPlans();
                        setCreateSubDialogOpen(true);
                      }}
                      disabled={!selectedAccountId}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Create Subscription
                    </Button>
                  </div>
                ) : (
                  <>
                    <DataTable
                      data={subscriptions}
                      columns={subscriptionsColumns}
                      showPagination={false}
                      innerScroll={false}
                    />
                    <ContactsPagination
                      currentPage={subsPage}
                      totalPages={subsTotalPages}
                      totalCount={subsTotal}
                      pageSize={subsPageSize}
                      onPageChange={setSubsPage}
                      onPageSizeChange={(size) => {
                        setSubsPageSize(size);
                        setSubsPage(1);
                      }}
                      isLoading={loadingSubscriptions}
                      itemLabel="subscriptions"
                      pageSizeOptions={[10, 20]}
                      minPageSize={10}
                      hideTotalCount
                    />
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

      </Tabs >

      {/* Create Payment Link Dialog */}
      < Dialog open={createLinkDialogOpen} onOpenChange={setCreateLinkDialogOpen} >
        <ScrollableDialogContent
          className="sm:max-w-[500px]"
          header={
            <DialogHeader>
              <DialogTitle>Create Payment Link</DialogTitle>
              <DialogDescription>
                Create a payment link to share with customers
              </DialogDescription>
            </DialogHeader>
          }
          footer={
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateLinkDialogOpen(false)} disabled={creatingLink}>
                Cancel
              </Button>
              <Button onClick={handleCreatePaymentLink} disabled={creatingLink || !linkForm.amount || !linkAmountCheck.ok}>
                {creatingLink ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <LinkIcon className="h-4 w-4 mr-2" />
                    Create Link
                  </>
                )}
              </Button>
            </DialogFooter>
          }
        >
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="link-amount">Amount *</Label>
              <div className="flex gap-2">
                <Input
                  id="link-amount"
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={MAX_PAYMENT_LINK_AMOUNT}
                  placeholder="0.00"
                  value={linkForm.amount}
                  onChange={(e) => setLinkForm({ ...linkForm, amount: e.target.value })}
                  className="flex-1"
                  aria-invalid={!!linkAmountError}
                />
                <select
                  value={linkForm.currency}
                  onChange={(e) => setLinkForm({ ...linkForm, currency: e.target.value })}
                  className="px-3 py-2 border rounded-md"
                >
                  <option value="INR">INR</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                </select>
              </div>
              {linkAmountError ? (
                <p className="text-sm text-destructive">{linkAmountError}</p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Up to {MAX_PAYMENT_LINK_AMOUNT.toLocaleString('en-IN')} {linkForm.currency}.
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="link-description">Description</Label>
              <Textarea
                id="link-description"
                placeholder="Payment description (optional)"
                value={linkForm.description}
                onChange={(e) => setLinkForm({ ...linkForm, description: e.target.value })}
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label>Customer Details (Optional)</Label>
              <Input
                placeholder="Customer Name"
                value={linkForm.customerName}
                onChange={(e) => setLinkForm({ ...linkForm, customerName: e.target.value })}
              />
              <Input
                type="email"
                placeholder="Customer Email"
                value={linkForm.customerEmail}
                onChange={(e) => setLinkForm({ ...linkForm, customerEmail: e.target.value })}
              />
              <Input
                placeholder="Customer Phone"
                value={linkForm.customerContact}
                onChange={(e) => setLinkForm({ ...linkForm, customerContact: e.target.value })}
              />
            </div>
            <div className="space-y-3">
              <Label>Notification Settings</Label>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="link-notify-sms"
                  checked={linkForm.notifySms}
                  onCheckedChange={(checked) => setLinkForm({ ...linkForm, notifySms: checked === true })}
                />
                <Label htmlFor="link-notify-sms" className="font-normal cursor-pointer">
                  Send SMS notification
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="link-notify-email"
                  checked={linkForm.notifyEmail}
                  onCheckedChange={(checked) => setLinkForm({ ...linkForm, notifyEmail: checked === true })}
                />
                <Label htmlFor="link-notify-email" className="font-normal cursor-pointer">
                  Send Email notification
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="link-reminder"
                  checked={linkForm.reminderEnable}
                  onCheckedChange={(checked) => setLinkForm({ ...linkForm, reminderEnable: checked === true })}
                />
                <Label htmlFor="link-reminder" className="font-normal cursor-pointer">
                  Enable payment reminders
                </Label>
              </div>
            </div>
          </div>
        </ScrollableDialogContent>
      </Dialog >

      {/* Create Plan Dialog */}
      <Dialog open={createPlanDialogOpen} onOpenChange={setCreatePlanDialogOpen}>
        <ScrollableDialogContent
          className="sm:max-w-[500px]"
          header={
            <DialogHeader>
              <DialogTitle>Create Plan</DialogTitle>
              <DialogDescription>
                Create a recurring billing plan
              </DialogDescription>
            </DialogHeader>
          }
          footer={
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreatePlanDialogOpen(false)} disabled={creatingPlan}>
                Cancel
              </Button>
              <Button onClick={handleCreatePlan} disabled={creatingPlan || !planForm.name || !planForm.amount}>
                {creatingPlan ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Package className="h-4 w-4 mr-2" />
                    Create Plan
                  </>
                )}
              </Button>
            </DialogFooter>
          }
        >
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="plan-name">Plan Name *</Label>
              <Input
                id="plan-name"
                placeholder="e.g. Monthly Pro Plan"
                value={planForm.name}
                onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="plan-amount">Amount *</Label>
              <div className="flex gap-2">
                <Input
                  id="plan-amount"
                  type="number"
                  step="0.01"
                  min="0.01"
                  placeholder="0.00"
                  value={planForm.amount}
                  onChange={(e) => setPlanForm({ ...planForm, amount: e.target.value })}
                  className="flex-1"
                />
                <select
                  value={planForm.currency}
                  onChange={(e) => setPlanForm({ ...planForm, currency: e.target.value })}
                  className="px-3 py-2 border rounded-md"
                >
                  <option value="INR">INR</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="plan-period">Billing Period *</Label>
                <Select value={planForm.period} onValueChange={(value: any) => setPlanForm({ ...planForm, period: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select period" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="yearly">Yearly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="plan-interval">Interval</Label>
                <Input
                  id="plan-interval"
                  type="number"
                  min="1"
                  placeholder="1"
                  value={planForm.interval}
                  onChange={(e) => setPlanForm({ ...planForm, interval: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="plan-description">Description</Label>
              <Textarea
                id="plan-description"
                placeholder="Plan description (optional)"
                value={planForm.description}
                onChange={(e) => setPlanForm({ ...planForm, description: e.target.value })}
                rows={3}
              />
            </div>
          </div>
        </ScrollableDialogContent>
      </Dialog>

      {/* Create Subscription Dialog */}
      <Dialog open={createSubDialogOpen} onOpenChange={setCreateSubDialogOpen}>
        <ScrollableDialogContent
          className="sm:max-w-[500px]"
          header={
            <DialogHeader>
              <DialogTitle>Create Subscription</DialogTitle>
              <DialogDescription>
                Create a new subscription for a customer
              </DialogDescription>
            </DialogHeader>
          }
          footer={
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateSubDialogOpen(false)} disabled={creatingSub}>
                Cancel
              </Button>
              <Button onClick={handleCreateSubscription} disabled={creatingSub || !subForm.planId || !subForm.totalCount}>
                {creatingSub ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <CalendarClock className="h-4 w-4 mr-2" />
                    Create Subscription
                  </>
                )}
              </Button>
            </DialogFooter>
          }
        >
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="sub-plan">Plan *</Label>
              <Select value={subForm.planId} onValueChange={(value) => setSubForm({ ...subForm, planId: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a plan" />
                </SelectTrigger>
                <SelectContent>
                  {plans.map((plan) => (
                    <SelectItem key={plan.id} value={plan.id}>
                      {plan.item?.name} - {new Intl.NumberFormat('en-IN', {
                        style: 'currency',
                        currency: plan.item?.currency || 'INR',
                      }).format((plan.item?.amount || 0) / 100)} / {plan.period}
                    </SelectItem>
                  ))}
                  {plans.length === 0 && (
                    <SelectItem value="" disabled>
                      No plans available — create one first
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="sub-total-count">Total Billing Cycles *</Label>
                <Input
                  id="sub-total-count"
                  type="number"
                  min="1"
                  placeholder="e.g. 12"
                  value={subForm.totalCount}
                  onChange={(e) => setSubForm({ ...subForm, totalCount: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sub-quantity">Quantity</Label>
                <Input
                  id="sub-quantity"
                  type="number"
                  min="1"
                  placeholder="1"
                  value={subForm.quantity}
                  onChange={(e) => setSubForm({ ...subForm, quantity: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="sub-offer">Offer ID (Optional)</Label>
              <Input
                id="sub-offer"
                placeholder="offer_xxxxxxxxxxxxx"
                value={subForm.offerId}
                onChange={(e) => setSubForm({ ...subForm, offerId: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                Paste an Offer ID from the Razorpay Dashboard (Subscriptions → Offers) to apply a discount.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="sub-customer">Customer ID (Optional)</Label>
              <Input
                id="sub-customer"
                placeholder="cust_xxxxxxxxxxxxx"
                value={subForm.customerId}
                onChange={(e) => setSubForm({ ...subForm, customerId: e.target.value })}
              />
            </div>
          </div>
        </ScrollableDialogContent>
      </Dialog>
    </div >
  );
};

export default RazorpayPage;

